import Button from '@/components/Button.jsx';
import { FiUpload, FiX } from '@/components/react-icons/main';
import IconV2 from '@/components/v2/IconV2.jsx';
import Stack from '@/components/v3/Stack.jsx';
import sendToast from '@/lib/sendToast';
import { useRef } from 'react';

/* fileMaxSize is the platform-wide argument contract and is expressed in megabytes
   (see projects/shared/workflow/castArgumentDataType.js) — keep that unit here. */
const DEFAULT_MAX_FILE_SIZE_MB = 0.0625;
const BYTES_PER_MB = 1024 * 1024;

/* The picked file never leaves the browser as a file: it is base64-encoded here and saved
   through the normal per-key secret path, landing in the encrypted credential secrets store. */
const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const formatSize = (bytes) => (bytes >= 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${bytes} B`);

const CredentialFileField = ({ field, value, onChange, disabled }) => {
  const inputRef = useRef(null);
  const maxFileBytes = (field?.fileMaxSize || DEFAULT_MAX_FILE_SIZE_MB) * BYTES_PER_MB;
  const hasValue = typeof value === 'string' && value.length > 0;
  const storedSize = hasValue ? Math.floor((value.replace(/\s|=+$/g, '').length * 3) / 4) : 0;

  const handleFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) {
      return;
    }

    if (file.size > maxFileBytes) {
      sendToast({ message: `File is too large (${formatSize(file.size)}). ${field?.name || 'This field'} accepts files up to ${formatSize(maxFileBytes)}.`, duration: 4000 });
      return;
    }

    try {
      const base64 = await readFileAsBase64(file);
      onChange?.(base64);
    } catch (error) {
      sendToast({ message: `Could not read ${file.name}. ${error?.message || 'Try selecting the file again.'}`, duration: 4000 });
    }
  };

  return (
    <Stack className={'comp-credential-file-field'} gap={8} align={'center'}>
      <input ref={inputRef} type={'file'} hidden onChange={handleFilePicked} disabled={disabled} />
      <Button size={'sm'} icon={FiUpload} disabled={disabled} onClick={() => inputRef.current?.click()}>
        {hasValue ? 'Replace' : 'Upload'}
      </Button>
      <span className={'file-status'} title={hasValue ? `File stored (${formatSize(storedSize)})` : 'No file uploaded'}>
        {hasValue ? formatSize(storedSize) : 'No file'}
      </span>
      {hasValue && !disabled && <IconV2 className={'clear-file light-medium'} icon={FiX} title={'Clear file'} onClick={() => onChange?.(null)} />}
    </Stack>
  );
};

export default CredentialFileField;
